'use client';

import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface SectorData {
  sector: string;
  shortName: string;
  change: number | null;
  volume: string | null;
  topMover: string | null;
  topMoverChange: number | null;
  hasData: boolean;
}

const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: SectorData }> }) => {
  if (active && payload && payload.length) {
    const d = payload[0].payload;
    return (
      <div className="bg-[#1E293B] border border-[#334155] rounded p-3 text-xs shadow-lg">
        <p className="font-semibold text-slate-200 mb-1">{d.sector}</p>
        {d.change != null ? (
          <p className={`font-mono font-bold ${d.change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {d.change >= 0 ? '+' : ''}{d.change.toFixed(2)}%
          </p>
        ) : (
          <p className="text-slate-600 font-mono">No data</p>
        )}
        {d.volume && <p className="text-slate-400 mt-1">Vol: {d.volume}</p>}
        {d.topMover && (
          <p className="text-slate-400">
            Top: <span className="text-sky-400">{d.topMover}</span>{' '}
            {d.topMoverChange != null && (
              <span className={d.topMoverChange >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                {d.topMoverChange >= 0 ? '+' : ''}{d.topMoverChange.toFixed(2)}%
              </span>
            )}
          </p>
        )}
      </div>
    );
  }
  return null;
};

export default function SectorPerformanceMatrix() {
  const [sectors, setSectors] = useState<SectorData[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'chart' | 'grid'>('chart');
  const [sortBy, setSortBy] = useState<'change' | 'volume'>('change');
  const supabase = createClient();

  const fetchSectors = async () => {
    try {
      const res = await fetch('/api/market/sectors');
      const json = await res.json();
      if (json.sectors) {
        setSectors(json.sectors);
      }
    } catch (err) {
      // Keep state
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSectors();

    const channel = supabase
      .channel('market_prices_sectors')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'market_prices' }, () => {
        fetchSectors();
      })
      .subscribe();

    const pollId = setInterval(fetchSectors, 30000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(pollId);
    };
  }, []);

  const sorted = [...sectors].sort((a, b) => {
    if (sortBy === 'change') {
      if (a.change == null && b.change == null) return 0;
      if (a.change == null) return 1;
      if (b.change == null) return -1;
      return b.change - a.change;
    }
    return 0;
  });

  const hasData = sectors.some(s => s.hasData);

  return (
    <div className="bg-[#1E293B] border border-[#334155] rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#334155]">
        <div className="flex items-center gap-2">
          <Icon name="Squares2X2Icon" size={16} variant="solid" className="text-amber-500" />
          <span className="text-sm font-semibold text-slate-200 font-mono">Sector Performance</span>
          {loading && <div className="w-3 h-3 border border-amber-500 border-t-transparent rounded-full animate-spin" />}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value as 'change' | 'volume')}
            className="text-xs bg-[#334155] text-slate-300 border border-[#475569] rounded px-2 py-1 outline-none"
          >
            <option value="change">Sort: Change</option>
            <option value="volume">Sort: Volume</option>
          </select>
          <div className="flex rounded overflow-hidden border border-[#334155]">
            <button
              onClick={() => setView('chart')}
              className={`px-2 py-1 text-xs transition-all ${view === 'chart' ? 'bg-sky-500 text-white' : 'bg-[#334155] text-slate-400 hover:text-slate-200'}`}
            >
              <Icon name="ChartBarIcon" size={12} variant="outline" />
            </button>
            <button
              onClick={() => setView('grid')}
              className={`px-2 py-1 text-xs transition-all ${view === 'grid' ? 'bg-sky-500 text-white' : 'bg-[#334155] text-slate-400 hover:text-slate-200'}`}
            >
              <Icon name="TableCellsIcon" size={12} variant="outline" />
            </button>
          </div>
        </div>
      </div>

      <div className="p-4">
        {!loading && !hasData && (
          <div className="flex items-center justify-center h-56 text-slate-600 font-mono text-xs text-center">
            <div>
              <Icon name="ChartBarIcon" size={32} variant="outline" className="mx-auto mb-2 text-slate-700" />
              <p>Sector data unavailable</p>
              <p className="text-slate-700 mt-1">Configure API key in Settings</p>
            </div>
          </div>
        )}

        {(loading || hasData) && view === 'chart' && (
          <div className="w-full h-56" aria-label="Sector Performance Bar Chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={sorted} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <XAxis
                  dataKey="shortName"
                  tick={{ fill: '#94A3B8', fontSize: 10, fontFamily: 'Fira Code' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: '#94A3B8', fontSize: 10, fontFamily: 'Fira Code' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={v => `${v}%`}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(51,65,85,0.4)' }} />
                <Bar dataKey="change" radius={[3, 3, 0, 0]}>
                  {sorted.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={entry.change == null ? '#334155' : entry.change >= 0 ? '#10B981' : '#EF4444'}
                      fillOpacity={entry.change == null ? 0.3 : 0.85}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {!loading && hasData && view === 'grid' && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {sorted.map(s => (
              <div
                key={s.sector}
                className={`p-2.5 rounded border ${
                  s.change == null ? 'border-slate-700 bg-slate-800/30' :
                  s.change >= 0 ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-red-500/20 bg-red-500/5'
                }`}
              >
                <p className="text-xs font-mono font-bold text-slate-300 truncate">{s.shortName}</p>
                <p className={`text-sm font-mono font-bold ${
                  s.change == null ? 'text-slate-600' : s.change >= 0 ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {s.change != null ? `${s.change >= 0 ? '+' : ''}${s.change.toFixed(2)}%` : '—'}
                </p>
                <p className="text-xs text-slate-500 font-mono">{s.topMover ?? '—'}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}